import _ from "lodash";
import React, { useState, useMemo, useCallback } from "react";
import { useI18n } from "Hooks";
import { Modal, Tabs, Tooltip, IconButton } from "@toddle-edu/ds-web";
import classes from "./OverviewAndSettingsModal.scss";
import classNames from "classnames";
import { copyToClipboard } from "Utils/CopyToClipboard";
import { setToastMsg } from "routes/modules/LoginModule";
import { useDispatch } from "Hooks/useDispatch";

import {
  CHAT_TYPE_ENUM,
  OVERVIEW_AND_SETTING_CHANNEL_TABS_CONFIG,
  OVERVIEW_AND_SETTING_CHANNEL_TABS_CONSTANTS,
  getPublicChannelLink,
  isChatToChannelEnabled,
  isChannelSplitFeatureEnabled,
  isMessagingRevampV2Enabled,
} from "MessagingV2/modules/Utils";

import { getHeaderChannelAvatarProps } from "MessagingV2/components/ChatContainer/Header/HeaderUtils";
import { AvatarWithAccessPoint } from "MessagingV2/components/ThreadList/Avatar";
import EditChannelTab from "./components/EditChannelTab";
import ViewChannelTab from "./components/ViewChannelTab";
import ManageMembersTab from "./components/ManageMembersTab";
import ManageMembersTabV2 from "./components/ManageMembersTabV2";
import ChannelSettingsTab from "./components/ChannelSettingsTab";
import ChannelSettingTabV2 from "./components/ChannelSettingsTabV2";
import ChatToChannel from "MessagingV2/components/ChatToChannel";
import ConditionalWrapper from "Utils/ConditionalWrapper";
import { isIdEqual } from "Utils/idHelpers";
import { CloseOutlined, CopyOutlined } from "@toddle-edu/ds-icons";
import { getIsParent, getUserId } from "modules/CommonSelectors";
import { getIsAdminByRole } from "lib/staffPermissionUtils";

const MODALS = {
  ADD_MEMBERS_OR_CREATE_CHANNEL_MODAL: "ADD_MEMBERS_OR_CREATE_CHANNEL_MODAL",
  OVERVIEW_AND_SETTINGS_MODAL: "OVERVIEW_AND_SETTINGS_MODAL",
};

const getShowViewChannelTab = ({
  isMemberUpdate,
  isUserPartOfChannel,
  channelType,
  canManageChannel,
}) => {
  const isDepartmentChannel = _.isEqual(
    channelType,
    CHAT_TYPE_ENUM.CHANNEL_DEPARTMENT
  );

  const channelSplitFeatureEnabled = isChannelSplitFeatureEnabled();

  if (isDepartmentChannel) {
    return true;
  }

  return (
    channelSplitFeatureEnabled && (!isMemberUpdate || !isUserPartOfChannel)
  );
};

const getCanMangeMembers = ({ channelType, canManageChannel, createdBy }) => {
  const isDepartmentChannel = _.isEqual(
    channelType,
    CHAT_TYPE_ENUM.CHANNEL_DEPARTMENT
  );

  if (isDepartmentChannel) {
    const userId = getUserId();
    return getIsParent() && isIdEqual(_.get(createdBy, "id"), userId);
  }
  return canManageChannel;
};

const OverviewAndSettingsModal = ({
  onCloseModal,
  chatId,
  channelLabel,
  channelDescription,
  channelBannerMessage,
  channelProfileImage,
  viewOnlyRoles,
  channelType,
  profileIcon,
  users,
  isMemberUpdate,
  defaultTab,
  canManageChannel,
  isUserPartOfChannel,
  isMuted,
  isMembersListVisible,
  memberListHiddenRoles,
  createdBy,
}) => {
  const { t } = useI18n(["common"]);
  const dispatch = useDispatch();
  const hideChannelMemberTab = !isMembersListVisible;

  const [showTooltip, setShowTooltip] = useState(false);

  const [showModalType, setShowModalType] = useState(
    MODALS.OVERVIEW_AND_SETTINGS_MODAL
  );

  const [currentTab, setCurrentTab] = useState(defaultTab);

  const avatarProps = getHeaderChannelAvatarProps({
    channelLabel,
    chatType: channelType,
    profileImage: channelProfileImage,
    profileIcon,
    chatId,
  });
  const usersCount = _.size(users);

  const filterTabs = useCallback(
    tab => {
      const channelSplitFeatureEnabled = isChannelSplitFeatureEnabled();

      switch (_.get(tab, "value", "")) {
        case OVERVIEW_AND_SETTING_CHANNEL_TABS_CONSTANTS.MEMBERS:
          return (
            !hideChannelMemberTab &&
            _.includes(_.get(tab, "visibleChatTypes", []), channelType)
          );
        case OVERVIEW_AND_SETTING_CHANNEL_TABS_CONSTANTS.SETTINGS:
          if (channelSplitFeatureEnabled) {
            return (
              _.includes(_.get(tab, "visibleChatTypes", []), channelType) &&
              isUserPartOfChannel
            );
          }
          return (
            _.includes(_.get(tab, "visibleChatTypes", []), channelType) &&
            canManageChannel
          );
        case OVERVIEW_AND_SETTING_CHANNEL_TABS_CONSTANTS.ABOUT:
          if (channelSplitFeatureEnabled) {
            return _.includes(_.get(tab, "visibleChatTypes", []), channelType);
          }

          return (
            _.includes(_.get(tab, "visibleChatTypes", []), channelType) &&
            canManageChannel
          );

        default:
          return false;
      }
    },
    [channelType, canManageChannel, isUserPartOfChannel]
  );

  const tabsConfig = useMemo(() => {
    return _.map(
      _.filter(_.values(OVERVIEW_AND_SETTING_CHANNEL_TABS_CONFIG), filterTabs),
      node => {
        const isMembersTab = _.isEqual(
          node.value,
          OVERVIEW_AND_SETTING_CHANNEL_TABS_CONSTANTS.MEMBERS
        );

        return isMembersTab
          ? {
              value: node.value,
              label: `${t(node.label)} (${usersCount})`,
            }
          : {
              value: node.value,
              label: t(node.label),
            };
      }
    );
  }, [t, usersCount, filterTabs]);

  const getShowAddMemberButton = channelType => {
    switch (channelType) {
      case CHAT_TYPE_ENUM.CHANNEL_DEPARTMENT: {
        const userId = getUserId();
        return getIsParent() && isIdEqual(_.get(createdBy, "id"), userId);
      }
      case CHAT_TYPE_ENUM.CHANNEL_SYSTEM_MANAGED:
        return false;
      case CHAT_TYPE_ENUM.CHANNEL_CUSTOM:
      case CHAT_TYPE_ENUM.CHANNEL_PUBLIC:
        return canManageChannel;
      case CHAT_TYPE_ENUM.CHANNEL_FAMILY:
        return isChatToChannelEnabled();
    }
  };

  const checkIfTextOverflow = useCallback(node => {
    if (node) {
      if (node.offsetWidth < node?.scrollWidth) {
        setShowTooltip(true);
      } else {
        setShowTooltip(false);
      }
    }
  }, []);

  const renderTabs = () => {
    const showAddMemberButton = getShowAddMemberButton(channelType);
    const channelSplitFeatureEnabled = isChannelSplitFeatureEnabled();

    switch (currentTab) {
      case OVERVIEW_AND_SETTING_CHANNEL_TABS_CONSTANTS.ABOUT: {
        const showViewChannelTab = getShowViewChannelTab({
          isMemberUpdate,
          isUserPartOfChannel,
          channelType,
          canManageChannel,
        });

        if (showViewChannelTab) {
          return (
            <ViewChannelTab
              channelDescription={channelDescription}
              channelBannerMessage={channelBannerMessage}
              channelLabel={channelLabel}
              createdBy={createdBy}
              channelType={channelType}
            />
          );
        }
        return (
          <EditChannelTab
            onCloseModal={onCloseModal}
            chatId={chatId}
            channelLabel={channelLabel}
            channelDescription={channelDescription}
            channelProfileImage={channelProfileImage}
            channelType={channelType}
            channelBannerMessage={channelBannerMessage}
          />
        );
      }
      case OVERVIEW_AND_SETTING_CHANNEL_TABS_CONSTANTS.MEMBERS: {
        return channelSplitFeatureEnabled ? (
          <ManageMembersTabV2
            chatId={chatId}
            channelType={channelType}
            isMemberUpdate={isMemberUpdate}
            canManageChannel={getCanMangeMembers({
              channelType,
              canManageChannel,
              createdBy,
            })}
            canBlockUser={
              (getIsAdminByRole() || canManageChannel) && isUserPartOfChannel
            }
            users={users}
            createdBy={createdBy}
            showAddMemberButton={showAddMemberButton && isUserPartOfChannel}
            onAddMember={showAddMemberORCreateUI}
          />
        ) : (
          <ManageMembersTab
            onCloseModal={onCloseModal}
            chatId={chatId}
            viewOnlyRoles={viewOnlyRoles}
            channelType={channelType}
            users={users}
            isMemberUpdate={isMemberUpdate}
            channelLabel={channelLabel}
            onAddMember={showAddMemberORCreateUI}
            showAddMemberButton={showAddMemberButton}
          />
        );
      }
      case OVERVIEW_AND_SETTING_CHANNEL_TABS_CONSTANTS.SETTINGS: {
        return channelSplitFeatureEnabled ? (
          <ChannelSettingTabV2
            chatId={chatId}
            createdBy={createdBy}
            canUpdateChannel={canManageChannel}
            onCloseModal={onCloseModal}
            viewOnlyRoles={viewOnlyRoles || []}
            channelType={channelType}
            isMuted={isMuted}
            memberListHiddenRoles={memberListHiddenRoles}
          />
        ) : (
          <ChannelSettingsTab
            onCloseModal={onCloseModal}
            chatId={chatId}
            viewOnlyRoles={viewOnlyRoles}
            channelType={channelType}
          />
        );
      }
    }
  };

  const showAddMemberORCreateUI = () => {
    setShowModalType(MODALS.ADD_MEMBERS_OR_CREATE_CHANNEL_MODAL);
  };

  const onCloseAddMemberUI = () => {
    setShowModalType(MODALS.OVERVIEW_AND_SETTINGS_MODAL);
  };

  const copyLinkToClipboard = () => {
    const channelLink = getPublicChannelLink({ chatId });
    copyToClipboard(channelLink, () => {
      dispatch(
        setToastMsg({
          msg: "toastMsgs:copied_capital",
          type: "success",
        })
      );
    });
  };

  const isPublicChannel = _.isEqual(channelType, CHAT_TYPE_ENUM.CHANNEL_PUBLIC);

  const isUIRevampV2Enabled = isMessagingRevampV2Enabled();
  const channelSplitFeatureEnabled = isChannelSplitFeatureEnabled();

  return (
    <>
      {_.isEqual(showModalType, MODALS.OVERVIEW_AND_SETTINGS_MODAL) ? (
        <Modal
          height={"600px"}
          width={"480px"}
          hasOverlay={true}
          shouldCloseOnEsc={true}
          shouldCloseOnOverlayClick={true}
          onClose={onCloseModal}
          isOpen={true}
          dsVersion="2.0"
        >
          <Modal.Header isClosable={!channelSplitFeatureEnabled}>
            <div className={classes.avatarDetailContainerV2}>
              <AvatarWithAccessPoint
                {...avatarProps}
                customAvatarStyles={{
                  size: isUIRevampV2Enabled ? "large" : "small",
                  shade: "light",
                  randomizeColor: true,
                }}
                customAvatarContainerClassName={classes.avatarContainer}
              />
              <div id="headerRef" className={classes.detailContainerWrapper}>
                <div className={classes.detailContainer}>
                  <ConditionalWrapper
                    condition={showTooltip}
                    wrapper={
                      <Tooltip tooltip={channelLabel} placement="bottom" />
                    }
                    type={"wrapperAsElement"}
                  >
                    <div
                      ref={checkIfTextOverflow}
                      className={classNames(
                        classes.headerTitle,
                        "text-label-l"
                      )}
                    >
                      {channelLabel}
                    </div>
                  </ConditionalWrapper>
                </div>
              </div>
              {isPublicChannel ? (
                <Tooltip
                  tooltip={t("common:copy_channel_link")}
                  placement="top"
                >
                  <IconButton
                    className={classes.channelLinkCopyButton}
                    icon={<CopyOutlined />}
                    variant="plain"
                    type="neutral"
                    dsVersion="2.0"
                    onClick={copyLinkToClipboard}
                  />
                </Tooltip>
              ) : null}
              {channelSplitFeatureEnabled ? (
                <IconButton
                  className={classes.closeOverviewModalButton}
                  icon={<CloseOutlined />}
                  variant="plain"
                  type="neutral"
                  dsVersion="2.0"
                  onClick={onCloseModal}
                />
              ) : null}
            </div>
          </Modal.Header>
          <div className={classes.tabWrapper}>
            <Tabs
              options={tabsConfig}
              value={currentTab}
              onChange={setCurrentTab}
              variant={"inline"}
              dsVersion="2.0"
              containerStyle={{ gap: "4px" }}
            />
          </div>
          {renderTabs()}
        </Modal>
      ) : null}

      {_.isEqual(showModalType, MODALS.ADD_MEMBERS_OR_CREATE_CHANNEL_MODAL) ? (
        <ChatToChannel
          onCloseModal={onCloseAddMemberUI}
          chatId={chatId}
          channelLabel={channelLabel}
          conversationType={channelType}
          users={users}
        />
      ) : null}
    </>
  );
};

export default OverviewAndSettingsModal;
